import { Injectable } from '@angular/core';
import { ClientStore } from 'src/app/stores/stores';

export type InitialViewType =
  | 'VIDEO'
  | 'CONSUMPTION_MODE'
  | 'PROMOTION'
  | 'CATALOG';

export interface ViewDecision {
  viewType: InitialViewType;
  clearEverything: boolean;
  routePath: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class InitialViewOrchestratorService {
  private readonly STORAGE_PREFIX = 'initialView';

  constructor() {}

  /**
   * Determines which view to show on initial load or reorder
   * @param store The current store
   * @param isReorder Whether this is a reorder flow (user completed an order and wants to order again)
   * @returns ViewDecision object with view type and routing information
   */
  determineInitialView(
    store: ClientStore | null,
    isReorder: boolean = false,
    currentOrderHasConsumptionMode?: boolean,
  ): ViewDecision {
    if (!store) {
      return this.createDecision('CATALOG', false, null);
    }

    // N4-M9 (card #9664): the per-alias `consumptionModeSelected` sessionStorage
    // flag is NOT a reliable gate on its own. When a previous customer's order
    // tear-down failed (the kiosk stayed on the catalog screen after that order
    // went SUBMITTED), the flag survived into the next customer's session, and a
    // fresh order was created without ever showing the selector — persisting an
    // order with no `properties.CONSUMPTION_MODE`. So when the caller positively
    // knows the *current* order has no consumption mode set
    // (`currentOrderHasConsumptionMode === false`), force the selector regardless
    // of the stale flag. `undefined` means "unknown" and preserves prior
    // flag-only behavior.
    const orderKnownMissingMode = currentOrderHasConsumptionMode === false;

    // First time visit flow
    if (!isReorder) {
      // Check video first (only shown on first visit)
      const hasVideo = this.hasVideoEnabled(store);
      const videoShown = this.isVideoShown(store.aliasName);

      if (hasVideo && !videoShown) {
        return this.createDecision('VIDEO', false, this.getVideoRoute(store));
      }

      // Check consumption mode
      const hasConsumption = this.hasConsumptionModeEnabled(store);
      const consumptionSelected = this.isConsumptionModeSelected(
        store.aliasName,
      );

      if (hasConsumption && (!consumptionSelected || orderKnownMissingMode)) {
        return this.createDecision(
          'CONSUMPTION_MODE',
          true,
          'consumption-mode',
        );
      }

      // Check promotion
      const hasPromotion = this.hasPromotionEnabled(store);
      const promotionShown = this.isPromotionShown(store.aliasName);

      if (hasPromotion && !promotionShown) {
        return this.createDecision('PROMOTION', true, 'promotion');
      }

      // Default to catalog
      return this.createDecision('CATALOG', false, null);
    }

    // Reorder flow - skip video, show consumption mode or promotion
    const hasConsumption = this.hasConsumptionModeEnabled(store);
    const hasPromotion = this.hasPromotionEnabled(store);
    const consumptionSelected = this.isConsumptionModeSelected(store.aliasName);

    // In reorder flow, if consumption mode is enabled but not yet selected
    // (or the current order positively lacks one), show it
    if (hasConsumption && (!consumptionSelected || orderKnownMissingMode)) {
      return this.createDecision('CONSUMPTION_MODE', true, 'consumption-mode');
    }

    // If consumption mode was already selected (or not needed), check for promotion
    if (hasPromotion) {
      return this.createDecision('PROMOTION', true, 'promotion');
    }

    // Default to catalog (clear basket for fresh start)
    return this.createDecision('CATALOG', true, null);
  }

  /**
   * Check if store has video enabled
   */
  hasVideoEnabled(store: ClientStore): boolean {
    // Check if store has inactivity video URL configured
    const hasInactivityVideo = !!(
      store?.settings?.INACTIVITY_VIDEO &&
      store?.settings?.INACTIVITY_VIDEO.trim().length > 0
    );
    // INACTIVITY_OPTION can be boolean true or string 'ENABLED'
    const hasInactivityEnabled =
      store?.settings?.INACTIVITY_OPTION === 'ENABLED' ||
      store?.settings?.INACTIVITY_OPTION === true;

    return hasInactivityVideo && hasInactivityEnabled;
  }

  /**
   * Check if store has consumption mode (eat-in/takeaway) enabled
   */
  hasConsumptionModeEnabled(store: ClientStore): boolean {
    const deliveryDisabled = !store?.settings?.DELIVERY_ADDRESS;
    const askConsumptionMode =
      store?.settings?.ORDER_CAPTURE_ASK_EAT_IN_TAKEAWAY;
    return deliveryDisabled && askConsumptionMode;
  }

  /**
   * Check if store has active promotion
   */
  hasPromotionEnabled(store: ClientStore): boolean {
    return !!(
      store?.promotion?.name && store?.promotion?.name.trim().length > 0
    );
  }

  /**
   * Get video route based on store settings
   */
  getVideoRoute(store: ClientStore): string | null {
    // Return the kiosk video route
    return 'kiosk-video';
  }

  /**
   * Check if video has been shown in current session
   */
  isVideoShown(storeAlias: string): boolean {
    return (
      sessionStorage.getItem(
        `${this.STORAGE_PREFIX}:videoShown:${storeAlias}`,
      ) === 'true'
    );
  }

  /**
   * Check if consumption mode has been selected
   */
  isConsumptionModeSelected(storeAlias: string): boolean {
    return (
      sessionStorage.getItem(
        `${this.STORAGE_PREFIX}:consumptionModeSelected:${storeAlias}`,
      ) === 'true'
    );
  }

  /**
   * Check if promotion has been shown in current session
   */
  isPromotionShown(storeAlias: string): boolean {
    return (
      sessionStorage.getItem(
        `${this.STORAGE_PREFIX}:promotionShown:${storeAlias}`,
      ) === 'true'
    );
  }

  /**
   * Mark video as shown
   */
  markVideoAsShown(storeAlias: string): void {
    sessionStorage.setItem(
      `${this.STORAGE_PREFIX}:videoShown:${storeAlias}`,
      'true',
    );
  }

  /**
   * Mark consumption mode as selected
   */
  markConsumptionModeAsSelected(storeAlias: string): void {
    sessionStorage.setItem(
      `${this.STORAGE_PREFIX}:consumptionModeSelected:${storeAlias}`,
      'true',
    );
  }

  /**
   * Mark promotion as shown
   */
  markPromotionAsShown(storeAlias: string): void {
    sessionStorage.setItem(
      `${this.STORAGE_PREFIX}:promotionShown:${storeAlias}`,
      'true',
    );
  }

  /**
   * Check if user is in reorder flow
   */
  isReorderFlow(storeAlias: string): boolean {
    return (
      sessionStorage.getItem(
        `${this.STORAGE_PREFIX}:isReorderFlow:${storeAlias}`,
      ) === 'true'
    );
  }

  /**
   * Set reorder flow flag
   * Note: In reorder flow, we keep consumption/promotion states as-is
   * (if already shown, don't show again; if not shown yet, show them)
   */
  setReorderFlow(storeAlias: string, isReorder: boolean): void {
    if (isReorder) {
      sessionStorage.setItem(
        `${this.STORAGE_PREFIX}:isReorderFlow:${storeAlias}`,
        'true',
      );
      // Don't clear consumption/promotion states - keep them as they are
      // This way, if user already saw them, they won't see them again
    } else {
      sessionStorage.removeItem(
        `${this.STORAGE_PREFIX}:isReorderFlow:${storeAlias}`,
      );
    }
  }

  /**
   * Clear all reorder state (called after order completion or session end)
   */
  clearReorderState(storeAlias: string): void {
    sessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:isReorderFlow:${storeAlias}`,
    );
  }

  /**
   * Check if the store has the Eat-In/Take-Away pickup-only configuration
   * (ORDER_CAPTURE_ASK_EAT_IN_TAKEAWAY enabled AND no delivery-to-address).
   * Equivalent to the predicate that `hasConsumptionModeEnabled` already
   * encodes — exposed under its own name so callers that care about the
   * "default deliveryMethod must be NO_LOCATION" rule do not need to
   * re-derive it from `store.settings`. Returns false when `store` is
   * null/undefined.
   */
  isEatInTakeAwayPickupOnly(store: ClientStore | null): boolean {
    return this.hasConsumptionModeEnabled(store);
  }

  /**
   * Persist the "this store is Eat-In/Take-Away pickup-only" verdict for the
   * current browser session so race-prone callers (notably
   * `OrderService.getInitOrder()`) can resolve the default `deliveryMethod`
   * without waiting on the async `selectedStore` Observable to have emitted.
   *
   * Set this once per session, as soon as the store's settings load
   * successfully and the predicate evaluates true. Do not unset on inactivity
   * / kiosk-video / thank-you-page restarts — the same physical store still
   * has the same configuration. Only `clearAllState()` (real session end)
   * clears it.
   */
  markEatInTakeAwayPickupOnly(storeAlias: string): void {
    if (!storeAlias) {
      return;
    }
    sessionStorage.setItem(
      `${this.STORAGE_PREFIX}:eatInTakeAwayPickupOnly:${storeAlias}`,
      'true',
    );
  }

  /**
   * Read the "Eat-In/Take-Away pickup-only" sessionStorage flag for a known
   * alias. Returns false when the alias is missing or the flag was never set.
   */
  isEatInTakeAwayPickupOnlyByAlias(storeAlias: string): boolean {
    if (!storeAlias) {
      return false;
    }
    return (
      sessionStorage.getItem(
        `${this.STORAGE_PREFIX}:eatInTakeAwayPickupOnly:${storeAlias}`,
      ) === 'true'
    );
  }

  /**
   * Race-safe variant for callers that cannot read the current store alias
   * cheaply — e.g. `OrderService.getInitOrder()` which is invoked while the
   * NgRx `selectedStore` slice is in flux. Walks `sessionStorage` for any
   * matching flag; one browser session is bound to one store, so any match
   * unambiguously identifies the current store.
   */
  isEatInTakeAwayPickupOnlyForCurrentSession(): boolean {
    const prefix = `${this.STORAGE_PREFIX}:eatInTakeAwayPickupOnly:`;
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (
        key &&
        key.startsWith(prefix) &&
        sessionStorage.getItem(key) === 'true'
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Clear the Eat-In/Take-Away pickup-only flag for the given alias.
   * Called from `clearAllState()` — kept as a discrete method so tests can
   * exercise it independently.
   */
  clearEatInTakeAwayPickupOnlyState(storeAlias: string): void {
    sessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:eatInTakeAwayPickupOnly:${storeAlias}`,
    );
  }

  /**
   * Clear consumption mode selected state
   */
  clearConsumptionModeState(storeAlias: string): void {
    sessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:consumptionModeSelected:${storeAlias}`,
    );
  }

  /**
   * Clear promotion shown state
   */
  clearPromotionState(storeAlias: string): void {
    sessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:promotionShown:${storeAlias}`,
    );
  }

  /**
   * Clear all session state for a store (useful for testing or logout)
   */
  clearAllState(storeAlias: string): void {
    sessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:videoShown:${storeAlias}`,
    );
    sessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:consumptionModeSelected:${storeAlias}`,
    );
    sessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:promotionShown:${storeAlias}`,
    );
    sessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:isReorderFlow:${storeAlias}`,
    );
    sessionStorage.removeItem(
      `${this.STORAGE_PREFIX}:eatInTakeAwayPickupOnly:${storeAlias}`,
    );
  }

  /**
   * Helper to create ViewDecision object
   */
  private createDecision(
    viewType: InitialViewType,
    clearEverything: boolean,
    routePath: string | null,
  ): ViewDecision {
    return {
      viewType,
      clearEverything,
      routePath,
    };
  }
}
